import React from 'react';
import type { VoiceBlobStyle } from '@brandclub/bc-widgets';

import { useVoicePersona } from '../../theme/useVoicePersona';
import { AuroraBlob } from './AuroraBlob';
import { MetaballBlob } from './MetaballBlob';
import { OrganicBlob } from './OrganicBlob';
import { ParticleBlob } from './ParticleBlob';
import { RippleBlob } from './RippleBlob';
import { WaveformBlob } from './WaveformBlob';
import { useBlobLevel } from './useBlobLevel';
import type {
  AuroraColors,
  BlobColorsByStyle,
  BlobState,
  MetaballColors,
  OrganicColors,
  ParticleColors,
  RippleColors,
  WaveformColors,
} from './types';

export type { BlobState };
export type { VoiceBlobStyle };
export type {
  AuroraColors,
  BlobColorsByStyle,
  MetaballColors,
  OrganicColors,
  ParticleColors,
  RippleColors,
  WaveformColors,
};

export interface VoiceBlobProps {
  /** State drives the envelope shape (idle / connecting / listening / thinking / tool / muted / error / ended). */
  state: BlobState;
  /** Pixel diameter. Defaults to 240. */
  size?: number;
  /** Explicit style override (e.g. from the Studio live preview). Falls back to brand config. */
  styleOverride?: VoiceBlobStyle;
  /** Optional color override for the active style. Studio passes the colors
   *  matching `styleOverride`; live overlay leaves this undefined to keep
   *  the per-blob defaults. */
  colors?: BlobColorsByStyle[VoiceBlobStyle];
}

/**
 * Top-level voice blob. Reads `blobStyle` from the brand persona (with a
 * preview-time override) and renders the matching variant. The shared
 * `useBlobLevel(state)` envelope feeds all six implementations so they
 * react consistently to state changes.
 *
 * Replaces the legacy `<Orb>` component. The six styles are ports of the
 * gallery in `voice-chat-wiki/mocks/blob-styles.tsx`.
 */
export const VoiceBlob: React.FC<VoiceBlobProps> = ({ state, size = 240, styleOverride, colors }) => {
  const persona = useVoicePersona();
  const effectiveStyle: VoiceBlobStyle = styleOverride ?? persona.blobStyle;
  const level = useBlobLevel(state);

  // Resolution order for colors:
  //   1. explicit `colors` prop (Studio gallery cards pass per-card overrides)
  //   2. persona.blobColorsAll[effectiveStyle] (persisted brand config, with
  //      DEFAULT_BLOB_COLORS already merged in `useVoicePersona`)
  // Callers that pass a `styleOverride` (the Studio preview) without `colors`
  // get the persisted colors for that style — matching the gallery's
  // per-style preview semantics.
  const effectiveColors = colors ?? persona.blobColorsAll[effectiveStyle];

  // The `colors` prop is only valid for the active style; we forward it
  // untyped per-branch because the discriminated union is keyed by
  // `styleOverride` and TypeScript can't narrow through that here.
  let inner: React.ReactNode;
  switch (effectiveStyle) {
    case 'organic':
      inner = <OrganicBlob level={level} size={size} colors={effectiveColors as OrganicColors} />;
      break;
    case 'particle':
      inner = <ParticleBlob level={level} size={size} colors={effectiveColors as ParticleColors} />;
      break;
    case 'waveform':
      inner = <WaveformBlob level={level} size={size} colors={effectiveColors as WaveformColors} />;
      break;
    case 'ripple':
      inner = <RippleBlob level={level} size={size} colors={effectiveColors as RippleColors} />;
      break;
    case 'metaball':
      inner = <MetaballBlob level={level} size={size} colors={effectiveColors as MetaballColors} />;
      break;
    case 'aurora':
    default:
      inner = <AuroraBlob level={level} size={size} colors={effectiveColors as AuroraColors} />;
      break;
  }

  return (
    <div
      style={{
        width: size,
        height: size,
        display: 'grid',
        placeItems: 'center',
        // Keep aurora blend modes inside the orb (light page bg otherwise washes to white).
        isolation: effectiveStyle === 'aurora' ? 'isolate' : undefined,
      }}
      aria-label={`Voice blob (${state})`}
      role="img"
    >
      {inner}
    </div>
  );
};

export default VoiceBlob;
